// src/components/ActivitiesTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './ActivitiesTab.css';
import LogActivityModal from './LogActivityModal';

const ACTIVITY_TYPES = ['Email Sent', 'Meeting Held', 'Proposal Sent', 'Call Made', 'Follow-up Completed', 'LinkedIn Message'];

const TYPE_CONFIG = {
  'Email Sent':          { color: 'var(--dm-indigo)', bg: '#EEF2FF', icon: '✉️' },
  'Meeting Held':        { color: 'var(--dm-green)', bg: 'var(--dm-green-light)', icon: '🤝' },
  'Proposal Sent':       { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', icon: '📄' },
  'Call Made':           { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', icon: '📞' },
  'Follow-up Completed': { color: 'var(--dm-indigo-soft)', bg: '#EEF2FF', icon: '✅' },
  'LinkedIn Message':    { color: '#0A66C2', bg: '#E8F0FE', icon: '🔗' },
};

const STATUS_CONFIG = {
  'Awaiting Response':    { color: '#EAB308', bg: '#FEF9C3' },
  'Response Received':    { color: 'var(--dm-green-soft)', bg: 'var(--dm-green-wash)' },
  'Follow-up Created':    { color: 'var(--dm-indigo)', bg: '#EEF2FF' },
  'No Follow-up Needed':  { color: 'var(--dm-grey-600)', bg: '#F3F4F6' },
};

const EMPTY_FORM = {
  name: '',
  activityType: '',
  activityDate: '',
  status: 'Awaiting Response',
  needsFollowup: false,
  followupDue: '',
  followupType: '',
};

const FOLLOWUP_TYPES = [
  'Call', 'Email', 'Send Collateral', 'Create Proposal', 'Schedule Meeting', 'Follow-up',
];

function ActivitiesTab({ accountId, accountName = 'Account' }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [addAnother, setAddAnother] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);

  const fetchActivities = useCallback(async () => {
    if (!accountId || accountId === 'undefined' || accountId === 'null') { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await apiFetch(`/api/activities/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setActivities(data.activities);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load activities');
      console.error('Activities error:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const showToast = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCardClick = (activity) => {
    setSelectedActivity(activity);
    setModalMode('view');
  };

  const handleAddClick = () => {
    setShowLogModal(true);
  };

  const handleEditClick = (activity) => {
    setFormData({
      name: activity.name || '',
      activityType: activity.activityType || '',
      activityDate: activity.activityDate || '',
      status: activity.status || 'Awaiting Response',
      needsFollowup: activity.needsFollowup || false,
      followupDue: activity.followupDue || '',
      followupType: activity.followupType || '',
    });
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setSelectedActivity(null);
    setModalMode('view');
    setFormData(EMPTY_FORM);
    setSuccessMessage('');
    setShowDeleteConfirm(false);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.activityType) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        const res = await apiFetch('/api/activities/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId, ...formData }),
        });
        const data = await res.json();
        if (data.success) {
          // Auto-create follow-up task if requested
          if (formData.needsFollowup && formData.followupDue) {
              const actionLabel = formData.followupType || 'Follow up';
              await apiFetch('/api/tasks/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: actionLabel + ' — ' + formData.name + (accountName ? ' at ' + accountName : ''),
                  accountId,
                  dueDate: formData.followupDue,
                  taskType: formData.followupType || 'Follow-up',
                  priority: 'Do Today',
                  status: 'Suggested',
                }),
              });
            }
          await fetchActivities();
          showToast(`✅ Activity logged!`);
          if (addAnother) {
            setFormData(EMPTY_FORM);
          } else {
            handleCloseModal();
          }
        } else {
          alert('Failed to create: ' + data.error);
        }
      } else if (modalMode === 'edit') {
        const res = await apiFetch('/api/activities/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: selectedActivity.id, ...formData }),
        });
        const data = await res.json();
        if (data.success) {
          await fetchActivities();
          showToast(`✅ Activity updated!`);
          handleCloseModal();
        } else {
          alert('Failed to update: ' + data.error);
        }
      }
    } catch (err) {
      alert('Error saving activity');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedActivity) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/activities/delete?id=${selectedActivity.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchActivities();
        showToast(`🗑️ Activity deleted`);
        handleCloseModal();
      } else {
        alert('Failed to delete: ' + data.error);
      }
    } catch (err) {
      alert('Error deleting activity');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="act-loading"><div className="spinner" /><p>Loading activities...</p></div>;
  if (error)   return <div className="act-error"><p>⚠️ {error}</p></div>;

  return (
    <div className="act-container">

      {successMessage && <div className="act-toast">{successMessage}</div>}

      {/* Header */}
      <div className="act-header">
        <div className="act-header-left">
          <h3>Activities <span className="act-count">({activities.length})</span></h3>
        </div>
        <button className="btn-add-act" onClick={handleAddClick}>+ Log Activity</button>
      </div>

      {/* Activities Feed */}
      {activities.length === 0 ? (
        <div className="act-empty">
          <p>No activities logged yet for {accountName}.</p>
          <button className="btn-add-act-large" onClick={handleAddClick}>+ Log First Activity</button>
        </div>
      ) : (
        <div className="act-feed">
          {activities.map(activity => {
            const typeCfg = TYPE_CONFIG[activity.activityType] || { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '📋' };
            const statusCfg = STATUS_CONFIG[activity.status] || { color: 'var(--dm-grey-600)', bg: '#F3F4F6' };

            return (
              <div
                key={activity.id}
                className="act-card"
                style={{ borderLeftColor: typeCfg.color }}
                onClick={() => handleCardClick(activity)}
              >
                {/* Card Header */}
                <div className="act-card-header">
                  <div className="act-card-type" style={{ background: typeCfg.bg, color: typeCfg.color }}>
                    {typeCfg.icon} {activity.activityType}
                  </div>
                  <div className="act-card-status" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                    {activity.status}
                  </div>
                </div>

                {/* Activity Name */}
                <div className="act-card-name">{activity.name}</div>

                {/* Footer meta */}
                <div className="act-card-footer">
                  {activity.activityDate && (
                    <span className="act-date">
                      📅 {new Date(activity.activityDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {activity.needsFollowup && (
                    <span className="act-followup-badge">⚠️ Follow-up needed</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {(selectedActivity || modalMode === 'add') && (
        <div className="act-modal-overlay" onClick={handleCloseModal}>
          <div className="act-modal" onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="act-modal-header">
              <h3>
                {modalMode === 'add'  ? '+ Log Activity' :
                 modalMode === 'edit' ? 'Edit Activity' :
                 selectedActivity?.name}
              </h3>
              <button className="act-close-btn" onClick={handleCloseModal}>×</button>
            </div>

            {/* Modal Body */}
            <div className="act-modal-content">

              {/* VIEW MODE */}
              {modalMode === 'view' && selectedActivity && (
                <div className="act-view">
                  {/* Type + Status */}
                  <div className="act-view-pills">
                    {selectedActivity.activityType && (() => {
                      const cfg = TYPE_CONFIG[selectedActivity.activityType];
                      return (
                        <span className="act-view-pill" style={{ background: cfg?.bg, color: cfg?.color }}>
                          {cfg?.icon} {selectedActivity.activityType}
                        </span>
                      );
                    })()}
                    {selectedActivity.status && (() => {
                      const cfg = STATUS_CONFIG[selectedActivity.status];
                      return (
                        <span className="act-view-pill" style={{ background: cfg?.bg, color: cfg?.color }}>
                          {selectedActivity.status}
                        </span>
                      );
                    })()}
                  </div>

                  {/* Date */}
                  <div className="act-view-meta">
                    {selectedActivity.activityDate && (
                      <div className="act-view-field">
                        <label>Activity Date</label>
                        <div className="act-view-value">
                          {new Date(selectedActivity.activityDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    )}
                    {selectedActivity.contactName && (
                      <div className="act-view-field">
                        <label>Contact</label>
                        <div className="act-view-value">👤 {selectedActivity.contactName}</div>
                      </div>
                    )}
                    {selectedActivity.opportunityName && (
                      <div className="act-view-field">
                        <label>Opportunity</label>
                        <div className="act-view-value">🎯 {selectedActivity.opportunityName}</div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {selectedActivity.notes && (
                    <div className="act-view-section">
                      <label>Notes</label>
                      <div className="act-view-value act-view-notes">{selectedActivity.notes}</div>
                    </div>
                  )}

                  {/* Outcome */}
                  {selectedActivity.outcome && (
                    <div className="act-view-section">
                      <label>Outcome</label>
                      <div className="act-view-value">{selectedActivity.outcome}</div>
                    </div>
                  )}

                  {/* Follow-up */}
                  {selectedActivity.needsFollowup && (
                    <div className="act-view-section">
                      <label>Follow-up Needed</label>
                      <div className="act-followup-box">
                        ⚠️ Follow-up required
                        {selectedActivity.followupDue && (
                          <div className="act-followup-date">
                            Due: {new Date(selectedActivity.followupDue + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ADD / EDIT FORM */}
              {(modalMode === 'add' || modalMode === 'edit') && (
                <div className="act-form">

                  <div className="act-form-group">
                    <label>Activity Description *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => handleFormChange('name', e.target.value)}
                      placeholder="e.g., Follow-up call with David Carlson"
                      className="act-input"
                    />
                  </div>

                  <div className="act-form-row">
                    <div className="act-form-group">
                      <label>Activity Type *</label>
                      <select value={formData.activityType} onChange={e => handleFormChange('activityType', e.target.value)} className="act-select">
                        <option value="">- Select -</option>
                        {ACTIVITY_TYPES.map(t => (
                          <option key={t} value={t}>{TYPE_CONFIG[t].icon} {t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="act-form-group">
                      <label>Activity Date</label>
                      <input
                        type="date"
                        value={formData.activityDate}
                        onChange={e => handleFormChange('activityDate', e.target.value)}
                        className="act-input"
                      />
                    </div>
                  </div>

                  <div className="act-form-group">
                    <label>Status</label>
                    <select value={formData.status} onChange={e => handleFormChange('status', e.target.value)} className="act-select">
                      <option value="Awaiting Response">Awaiting Response</option>
                      <option value="Response Received">Response Received</option>
                      <option value="Follow-up Created">Follow-up Created</option>
                      <option value="No Follow-up Needed">No Follow-up Needed</option>
                    </select>
                  </div>

                  <div className="act-form-group">
                    <label className="act-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.needsFollowup}
                        onChange={e => handleFormChange('needsFollowup', e.target.checked)}
                      />
                      Needs Follow-up
                    </label>
                  </div>

                  {formData.needsFollowup && (
                    <div className="act-followup-fields">
                      <div className="act-form-group">
                        <label>Follow-up Action</label>
                        <select
                          value={formData.followupType}
                          onChange={e => handleFormChange('followupType', e.target.value)}
                          className="act-select"
                        >
                          <option value="">Select action...</option>
                          {FOLLOWUP_TYPES.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="act-form-group">
                        <label>Follow-up Due Date</label>
                        <input
                          type="date"
                          value={formData.followupDue}
                          onChange={e => handleFormChange('followupDue', e.target.value)}
                          className="act-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="act-modal-footer">
              {modalMode === 'view' && (
                <>
                  <button className="act-btn-delete" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
                  <div className="act-footer-right">
                    <button className="act-btn-cancel" onClick={handleCloseModal}>Close</button>
                    <button className="act-btn-edit" onClick={() => handleEditClick(selectedActivity)}>Edit</button>
                  </div>
                </>
              )}
              {(modalMode === 'add' || modalMode === 'edit') && (
                <>
                  {modalMode === 'add' && (
                    <label className="act-add-another">
                      <input type="checkbox" checked={addAnother} onChange={e => setAddAnother(e.target.checked)} />
                      Log & Add Another
                    </label>
                  )}
                  <div className="act-footer-right">
                    <button className="act-btn-cancel" onClick={handleCloseModal}>Cancel</button>
                    <button
                      className="act-btn-save"
                      onClick={handleSave}
                      disabled={saving || !formData.name.trim() || !formData.activityType}
                    >
                      {saving ? 'Saving...' : modalMode === 'add' ? 'Log Activity' : 'Save Changes'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Log Activity Modal */}
      {showLogModal && (
        <LogActivityModal
          accountId={accountId}
          accountName={accountName}
          onClose={() => setShowLogModal(false)}
          onSaved={async (result) => {
            setShowLogModal(false);
            await fetchActivities();
            const parts = ['✅ Activity logged!'];
            if (result.taskId)     parts.push('Task created.');
            if (result.cyranoQueued) parts.push('Draft sent to Staging.');
            if (result.calendarId) parts.push('Meeting added to calendar.');
            showToast(parts.join(' '));
          }}
        />
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="act-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="act-delete-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete Activity?</h3>
            <p>Are you sure you want to delete <strong>"{selectedActivity?.name}"</strong>? This cannot be undone.</p>
            <div className="act-delete-actions">
              <button className="act-btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="act-btn-delete-confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivitiesTab;
